/**
 * Remembers the last tenant across a session — story 0.5 AC-4.
 *
 * 🔒 **The URL always wins.** This only decides where to send someone who arrived
 * without an `idCliente` in the path. If persistence could override the URL, a
 * shared link would open on the recipient's last tenant instead of the one the
 * sender meant, which defeats AC-3.
 *
 * ⚠️ **Restoring is ALWAYS revalidated against the user's current `{clientes}`.**
 * The classic bug this prevents: a user's access to a client is revoked, they
 * return to the app, and the SPA cheerfully navigates them into a tenant they can
 * no longer see. The server would refuse every request and the screen would fill
 * with errors instead of saying what happened.
 *
 * `sessionStorage`, not `localStorage`: the handoff asks the selection to survive
 * "the session", and a tenant choice outliving the browser tab on a shared machine
 * is a worse default than re-picking.
 */
const CHAVE = 'iox.cliente'

export function lembrarCliente(idCliente: string): void {
  try {
    sessionStorage.setItem(CHAVE, idCliente)
  } catch {
    // Private mode, or storage disabled. Losing the preference is survivable;
    // failing the navigation over it is not.
  }
}

function lerGuardado(): string | null {
  try {
    return sessionStorage.getItem(CHAVE)
  } catch {
    return null
  }
}

/**
 * The remembered tenant, but ONLY if the user still has access to it.
 *
 * `permitidos` is the user's current `{clientes}` list — passing it is not
 * optional, which is what makes the revalidation impossible to forget.
 */
export function clienteLembrado(permitidos: readonly string[]): string | null {
  const guardado = lerGuardado()
  if (!guardado) return null
  if (!permitidos.includes(guardado)) {
    // Access was revoked while the value sat in storage. Drop it rather than
    // leaving a landmine for the next read.
    esquecerCliente()
    return null
  }
  return guardado
}

export function esquecerCliente(): void {
  try {
    sessionStorage.removeItem(CHAVE)
  } catch {
    // See lembrarCliente.
  }
}

/**
 * O cliente em foco para um usuário: o lembrado, senão o primeiro da lista dele.
 *
 * 🔒 Esta é a ÚNICA definição dessa precedência. Ela nasceu duplicada entre
 * `DestinoDeCliente` (que decide para onde `/` manda) e a `Sidebar` (que monta o
 * link de cada módulo), e a `Sidebar` tinha uma terceira resposta: string vazia.
 * O resultado foi `/cliente//finops`, que o router normaliza para
 * `/cliente/finops` — e a tela então reclamava de um "cliente chamado finops".
 *
 * `null` quando o usuário não tem cliente algum, que é uma configuração legítima
 * e não um erro: quem chama precisa decidir o que mostrar.
 */
export function clienteEmFoco(permitidos: readonly string[]): string | null {
  return clienteLembrado(permitidos) ?? permitidos[0] ?? null
}
